/**
 * 営業設定 > 書類期限タブ。
 * 見積有効期限の既定日数、請求支払期限の既定日数を編集する。
 *
 * 値は settings/sales に保存され、Cloud Functions の見積/請求作成ロジックで参照される。
 */
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useSalesSettingsWithDefaults,
  useUpdateSalesSettings,
} from '@/hooks/useSettings';

export function DocumentDeadlineTab() {
  const { merged, isLoading } = useSalesSettingsWithDefaults();
  const update = useUpdateSalesSettings();

  const [quoteValidDays, setQuoteValidDays] = useState<string>('');
  const [invoiceDueDays, setInvoiceDueDays] = useState<string>('');

  useEffect(() => {
    setQuoteValidDays(String(merged.quoteValidDays));
    setInvoiceDueDays(String(merged.invoiceDueDays));
  }, [merged.quoteValidDays, merged.invoiceDueDays]);

  const handleSave = async () => {
    const qd = Number(quoteValidDays);
    const id_ = Number(invoiceDueDays);
    if (!Number.isFinite(qd) || qd < 1 || qd > 365) {
      toast.error('見積有効期限日数は 1〜365 の範囲で入力してください');
      return;
    }
    if (!Number.isFinite(id_) || id_ < 1 || id_ > 365) {
      toast.error('請求支払期限日数は 1〜365 の範囲で入力してください');
      return;
    }
    try {
      await update.mutateAsync({
        quoteValidDays: Math.floor(qd),
        invoiceDueDays: Math.floor(id_),
      });
      toast.success('保存しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-lg space-y-5">
      <div>
        <Label htmlFor="quote-valid-days">見積有効期限の既定日数</Label>
        <div className="flex items-center gap-2">
          <Input
            id="quote-valid-days"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={quoteValidDays}
            onChange={(e) => setQuoteValidDays(e.target.value)}
            className="max-w-[120px]"
          />
          <span className="text-sm text-[var(--text-muted)]">日</span>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          見積書の発行日から有効期限までの日数。新規発行時に自動計算されます。デフォルト: 30 日。
        </p>
      </div>

      <div>
        <Label htmlFor="invoice-due-days">請求支払期限の既定日数</Label>
        <div className="flex items-center gap-2">
          <Input
            id="invoice-due-days"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={invoiceDueDays}
            onChange={(e) => setInvoiceDueDays(e.target.value)}
            className="max-w-[120px]"
          />
          <span className="text-sm text-[var(--text-muted)]">日</span>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          請求書の発行日から支払期限までの日数。デフォルト: 30 日。
        </p>
      </div>

      <div className="pt-2">
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={update.isPending}
        >
          {update.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}
